import type React from "react"
import { useState } from "react"

import { PageCard } from "@/components/page-card/page-card"
import { PageFilterCard } from "@/components/page-filter-card/page-filter-card"
import type { Meter } from "@/models/meter"
import type { ResourceName } from "@/models/resource"
import type { Site } from "@/models/site"
import { useSiteLayoutContext } from "@/modules/site/context/site-layout-context"
import {
  INTERVAL_USAGE_LIST_FILTERS,
  intervalDisplayUnit,
  toIntervalMeterIdentity,
  toMeterIdsQueryParam,
  toStackedSeries,
  useIntervalHourlyUsageProfile,
  useIntervalResourceScope,
  useIntervals,
} from "@/services/interval"
import { useMeters } from "@/services/meter/use-meters"
import { parseModelId } from "@/utils/parse-model-id"

import { Stack } from "@mui/material"

import { AverageHourlyUsageProfileTable } from "./average-hourly-usage-profile-table"
import { IntervalDataChart } from "./interval-data-chart"
import { IntervalDataFilterBar } from "./interval-data-filter-bar"
import { IntervalDataRecordsGrid } from "./interval-data-records-grid"
import {
  isIntervalChartWindowTooDense,
  resampleForGranularity,
  siteMidnightUtcIso,
} from "./interval-data-selection"
import { isIntervalVisualizationLoading } from "./is-interval-visualization-loading"
import { resolveIntervalChartView } from "./resolve-interval-chart-view"
import { useIntervalDataSelection } from "./use-interval-data-selection"

export const SiteIntervalDataRoute: React.FC = () => {
  const { orgId, site } = useSiteLayoutContext()

  if (!orgId || !site?.id) {
    return null
  }

  return (
    <SiteIntervalDataRouteContent
      key={site.id}
      organizationId={orgId}
      site={site}
    />
  )
}

interface SiteIntervalDataRouteContentProps {
  organizationId: string
  site: Site
}

const getSelectedMeterIfAvailable = (
  meters: Meter[],
  selectedMeter: Meter | null
): Meter | null => {
  if (selectedMeter == null) {
    return null
  }

  return meters.some((meter) => meter.id === selectedMeter.id)
    ? selectedMeter
    : null
}

const SiteIntervalDataRouteContent: React.FC<
  SiteIntervalDataRouteContentProps
> = ({ organizationId, site }) => {
  const siteIdNumber = parseModelId(site.id)
  const [selectedMeter, setSelectedMeter] = useState<Meter | null>(null)
  const [selectedResource, setSelectedResource] = useState<ResourceName | null>(
    null
  )

  const {
    metersData,
    metersIsError,
    metersIsPending,
    metersIsPlaceholderData,
  } = useMeters({
    apiQueryOptions: {
      includes: ["meter_source_type"],
      where: {
        organization_id: organizationId,
        site_id: site.id,
      },
    },
  })

  const metersNotReady = metersIsPending || metersIsPlaceholderData
  const scope = useIntervalResourceScope({
    meters: metersData,
    metersNotReady,
    organizationId,
    selectedResource,
    siteIdNumber,
  })
  const readyScope = scope.kind === "ready" ? scope : null
  const meters = readyScope?.meters ?? []
  const selectedMeterInScope = getSelectedMeterIfAvailable(
    meters,
    selectedMeter
  )
  const queryMeters = selectedMeterInScope ? [selectedMeterInScope] : meters
  const meterIds = toMeterIdsQueryParam(queryMeters)
  const meterIdentities = queryMeters.map((meter) =>
    toIntervalMeterIdentity(meter)
  )
  const showFlowDirection = readyScope?.showFlowDirection ?? false
  const isScopeLoading = scope.kind === "pending"

  const {
    dateRange,
    granularity,
    maxDate,
    minDate,
    setDateRange,
    setGranularity,
  } = useIntervalDataSelection({
    latestStartDatetime: readyScope?.latestStartDatetime ?? null,
    scopeIsPending: isScopeLoading,
    siteId: site.id ?? "",
    timeZone: site.timezone,
  })

  const resample = resampleForGranularity(granularity)
  const rangeIsValid = Boolean(
    dateRange.isValid && dateRange.start && dateRange.end
  )
  const isWindowTooDense = isIntervalChartWindowTooDense(dateRange, granularity)
  const rangeStart = siteMidnightUtcIso(dateRange.start, site.timezone)
  const rangeEnd = siteMidnightUtcIso(dateRange.end, site.timezone)
  const canRequestSelectedResource =
    readyScope != null && meterIds.length > 0 && rangeIsValid
  const isChartQueryEnabled = canRequestSelectedResource && !isWindowTooDense

  const rangeIntervals = useIntervals({
    apiQueryOptions: {
      ...INTERVAL_USAGE_LIST_FILTERS,
      end: rangeEnd,
      meter_ids: meterIds,
      site_id: siteIdNumber,
      sort: "start_datetime:asc",
      start: rangeStart,
      ...(resample ? { calendar: "local", resample } : {}),
    },
    organizationId,
    queryOptions: {
      enabled: isChartQueryEnabled,
      retry: false,
    },
  })

  const {
    intervalHourlyUsageProfileData,
    intervalHourlyUsageProfileIsError,
    intervalHourlyUsageProfileIsLoading,
  } = useIntervalHourlyUsageProfile({
    meterIds,
    organizationId,
    queryOptions: {
      enabled: canRequestSelectedResource,
      retry: false,
    },
    // BFF reads the YYYY-MM-DD prefix as a site-local calendar date.
    rangeEnd: dateRange.end?.toISODate() ?? "",
    rangeStart: dateRange.start?.toISODate() ?? "",
    siteId: site.id ?? "",
  })

  const rangeItems = rangeIntervals.intervalsData?.items ?? []
  const unit = intervalDisplayUnit(rangeItems) ?? readyScope?.unit
  const { data, series } = toStackedSeries(rangeItems, meterIdentities)
  const isLoading = isIntervalVisualizationLoading({
    hasReadyScope: readyScope != null,
    isQueryEnabled: isChartQueryEnabled,
    isQueryLoading: rangeIntervals.intervalsIsLoading,
    isScopeLoading,
    rangeIsValid,
  })
  const isProfileLoading = isIntervalVisualizationLoading({
    hasReadyScope: readyScope != null,
    isQueryEnabled: canRequestSelectedResource,
    isQueryLoading: intervalHourlyUsageProfileIsLoading,
    isScopeLoading,
    rangeIsValid,
  })
  const hasLoadError =
    metersIsError || scope.kind === "error" || rangeIntervals.intervalsIsError
  const emptyVariant = resolveIntervalChartView({
    fetchedPointCount: data.length,
    hasIntervalCapability: readyScope != null,
    hasLoadError,
    isWindowTooDense,
  })

  const handleResourceChange = (resource: ResourceName) => {
    setSelectedMeter(null)
    setSelectedResource(resource)
  }

  return (
    <Stack spacing={2}>
      <PageFilterCard>
        <IntervalDataFilterBar
          dateRange={dateRange}
          isScopeLoading={isScopeLoading}
          maxDate={maxDate}
          meters={meters}
          minDate={minDate}
          onDateRangeChange={setDateRange}
          onMeterChange={setSelectedMeter}
          onResourceChange={handleResourceChange}
          options={scope.options}
          selectedMeter={selectedMeterInScope}
          value={readyScope?.resource ?? null}
        />
      </PageFilterCard>
      <PageCard>
        <IntervalDataChart
          data={data}
          dateRange={dateRange}
          emptyVariant={emptyVariant}
          granularity={granularity}
          isLoading={isLoading}
          items={rangeItems}
          meters={meterIdentities}
          onGranularityChange={setGranularity}
          series={series}
          showFlowDirection={showFlowDirection}
          siteId={site.id ?? ""}
          siteName={site.name ?? ""}
          timeZone={site.timezone}
          unit={unit}
        />
      </PageCard>

      {scope.kind === "pending" || readyScope != null ? (
        <>
          <PageCard>
            <AverageHourlyUsageProfileTable
              dateRange={dateRange}
              isError={intervalHourlyUsageProfileIsError}
              isLoading={isProfileLoading}
              profile={intervalHourlyUsageProfileData}
              resource={readyScope?.resource}
              siteId={site.id ?? ""}
              unit={unit}
            />
          </PageCard>
          <PageCard>
            <IntervalDataRecordsGrid
              key={selectedResource ?? "default"}
              hasReadyScope={readyScope != null}
              isLoading={isScopeLoading}
              meters={queryMeters}
              organizationId={organizationId}
              rangeEnd={rangeEnd}
              rangeIsValid={rangeIsValid}
              rangeStart={rangeStart}
              showFlowDirection={showFlowDirection}
              siteId={site.id}
              timeZone={site.timezone}
            />
          </PageCard>
        </>
      ) : null}
    </Stack>
  )
}
